import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box as MuiBox,
  TextField as MuiTextField,
  Grid,
  Typography
} from "@mui/material";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import styled from "@emotion/styled";
import CommonPageLayout from "../../components/commonPageLayout/commonPageLayout";
import Loader from "../../components/loader/loader";
import CustomButton from "../../components/CommonButton/CommonButton";

const Box = styled(MuiBox)`
  display: flex;
  gap: 28px;
  margin-top: 32px;
`;

const TextField = styled(MuiTextField)`
  display: flex;
  padding-top: 1px;
  justify-content: center;
  align-items: center;

  label {
    color: var(--light-text-secondary, rgba(0, 0, 0, 0.6));
    font-feature-settings: "clig" off, "liga" off;
    font-family: "Montserrat", sans-serif;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 12px;
    letter-spacing: 0.15px;
  }

  input {
    color: var(--light-text-secondary, rgba(0, 0, 0, 0.6));
    font-feature-settings: "clig" off, "liga" off;
    font-family: "Montserrat", sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0.15px;
    &::before {
      height: 1px;
      background: rgba(0, 0, 0, 0.42);
    }
    &:hover:not(.Mui-disabled)::before {
      height: 1px;
      background: rgba(0, 0, 0, 0.42);
    }
  }
`;

interface PaymentDetailsResponse {
  Id: number;
  MerchantId: number;
  MerchantExternalReferenceSourceId: number;
  ExternalCustomerId: string;
  XeroCustomerId: string | null;
  LastModifiedDate: string;
  CreatedDateTime: string;
  MerchantExternalReferenceSource: string;
  CardNumber: string;
  CardType: string;
  CardExpirationMonth: number;
  CardExpirationYear: number;
  StripeCustomerId?: string; // Add Stripe customer ID if available
  ContractStatus?: string;
}

function PaymentDetails() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [details, setDetails] = useState<PaymentDetailsResponse | null>(null);

  // Function to get display name for billing type
  const getBillingTypeDisplayName = (billingType: string) => {
    switch (billingType?.toLowerCase()) {
      case "stripe":
        return "Credit Card";
      case "ezidebit":
        return "Direct Debit";
      case "invoice":
        return "Invoice";
      default:
        return billingType;
    }
  };

  let currentBusiness: any = localStorage?.getItem("currentBusiness");
  const state: any = {
    token: localStorage?.getItem("businessAccessToken"),
    currentBusiness: currentBusiness ? JSON.parse(currentBusiness) : null
  };

  const fetchPaymentDetails = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const url = `${baseUrl}/billingplatform-business/billingdetails/${state?.currentBusiness?.BusinessId}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${state?.token}`,
          "Content-Type": "application/json"
        }
      });
      console.log("Payment details API response:", response?.data ?? response);
      if (response?.data) {
        setDetails(response.data as PaymentDetailsResponse);
      }
    } catch (error: any) {
      console.log("Payment details API error:", error);
      setErrorMessage(
        error?.response?.data?.message || "Failed to fetch payment details"
      );
    } finally {
      setLoading(false);
    }
  }, [state?.token, state?.currentBusiness?.BusinessId]);

  useEffect(() => {
    fetchPaymentDetails();
  }, [fetchPaymentDetails]);

  // Function to handle card addition/editing
  const handleEditPayment = () => {
    setErrorMessage(null);
    setSuccessMessage(null);
    navigate("/card", {
      state: { returnUrl: "/account/paymentdetails" }
    });
  };

  // removed unused formatter; masking is done inline where needed

  return (
    <CommonPageLayout
      heading="Account"
      subHeading="Payment Details"
      docs={null}
    >
      {loading ? (
        <Loader />
      ) : (
        <>
          {errorMessage && (
            <Stack sx={{ width: "100%", mb: 2 }} spacing={2}>
              <Alert severity="error">{errorMessage}</Alert>
            </Stack>
          )}

          {successMessage && (
            <Stack sx={{ width: "100%", mb: 2 }} spacing={2}>
              <Alert severity="success">{successMessage}</Alert>
            </Stack>
          )}

          {!errorMessage && details && (
            <>
              {/* Edit Button - Top Right */}
              {details.MerchantExternalReferenceSource?.toLowerCase() &&
                details?.ContractStatus?.toLowerCase() === "active" && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mb: 2,
                      marginTop: "0px !important"
                    }}
                  >
                    <CustomButton
                      size="small"
                      variant="outlined"
                      className="blue_button"
                      onClick={handleEditPayment}
                    >
                      Edit
                    </CustomButton>
                  </Box>
                )}

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Billing Type"
                    value={getBillingTypeDisplayName(
                      details.MerchantExternalReferenceSource
                    )}
                    InputProps={{ readOnly: true }}
                    variant="standard"
                    disabled={true}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Created Date"
                    value={new Date(details.CreatedDateTime).toLocaleString()}
                    InputProps={{ readOnly: true }}
                    variant="standard"
                    disabled={true}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Last Modified Date"
                    value={new Date(details.LastModifiedDate).toLocaleString()}
                    InputProps={{ readOnly: true }}
                    variant="standard"
                    disabled={true}
                  />
                </Grid>

                {details?.MerchantExternalReferenceSource?.toLowerCase() ===
                "ezidebit" ? (
                  <>
                    <Grid item xs={12}>
                      <Typography
                        variant="h6"
                        sx={{ mb: 2, mt: 2, fontWeight: "bold" }}
                      >
                        Bank Account Details
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Account Name"
                        value={(details as any).AccountName || ""}
                        InputProps={{ readOnly: true }}
                        variant="standard"
                        disabled={true}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Account Number"
                        value={
                          (details as any).AccountNumber
                            ? "****" +
                              String((details as any).AccountNumber).slice(-4)
                            : "-"
                        }
                        InputProps={{ readOnly: true }}
                        variant="standard"
                        disabled={true}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Account BSB"
                        value={(details as any).AccountBSB || ""}
                        InputProps={{ readOnly: true }}
                        variant="standard"
                        disabled={true}
                      />
                    </Grid>
                  </>
                ) : details?.MerchantExternalReferenceSource?.toLowerCase() ===
                  "stripe" ? (
                  <>
                    <Grid item xs={12}>
                      <Typography
                        variant="h6"
                        sx={{ mb: 2, mt: 2, fontWeight: "bold" }}
                      >
                        Card Details
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Card Type"
                        value={
                          details.CardType
                            ? details.CardType.charAt(0).toUpperCase() +
                              details.CardType.slice(1)
                            : "-"
                        }
                        InputProps={{ readOnly: true }}
                        variant="standard"
                        disabled={true}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Card Number"
                        value={
                          details.CardNumber
                            ? `**** **** **** ${String(
                                details.CardNumber
                              ).slice(-4)}`
                            : "-"
                        }
                        InputProps={{ readOnly: true }}
                        variant="standard"
                        disabled={true}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Card Expiry"
                        value={
                          details.CardExpirationMonth &&
                          details.CardExpirationYear
                            ? `${String(details.CardExpirationMonth).padStart(
                                2,
                                "0"
                              )}/${details.CardExpirationYear}`
                            : "-"
                        }
                        InputProps={{ readOnly: true }}
                        variant="standard"
                        disabled={true}
                      />
                    </Grid>
                  </>
                ) : (
                  <></>
                )}
              </Grid>
            </>
          )}

          {!errorMessage && !details && (
            <Stack sx={{ width: "100%", mb: 2 }} spacing={2}>
              <Alert severity="info">No payment details available.</Alert>
            </Stack>
          )}
        </>
      )}
    </CommonPageLayout>
  );
}

export default PaymentDetails;
